import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  LinearProgress,
  Modal,
  Stack,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerStyles,
  ModalHeaderTitleStyles,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import {
  NoDataContainer,
  paginationLabel,
  StyledTableCell,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { CloseRounded } from "@mui/icons-material";
import { importConsentBlocks } from "../../../Redux/actions/consentAction";

type Props = {
  showModal: boolean;
  closeModal: () => void;
};

const ImportConsentBlocksModal = ({ showModal, closeModal }: Props) => {
  const dispatch = useAppDispatch();
  const [tableLoader, setTableLoader] = useState(false);
  const [instruments, setInstruments] = useState([]);
  const [instrumentsCount, setInstrumentsCount] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  // const [filterId, setFilterId] = useState("");
  const [page, setPage] = useState<number>(0);
  // const [instrumentDetails, setInstrumentDetails] = useState<any>(null);
  const [buttonLoader, setButtonLoader] = useState("");
  const formType = useAppSelector((state) => state.consent.formType);

  const fetchInstruments = useCallback(
    async (page: number, searchText?: string) => {
      try {
        setTableLoader(true);
        let url = `/instrument-library?page=${
          page + 1
        }&size=10&status=active&type=${
          formType === "sunshine" ? "sunshine" : "econsent"
        }`;
        if (searchText) {
          url += `&title=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.rows;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          title: item?.title || "",
          fileUrl: item?.fileUrl || "",
          status: item?.status || "",
          description: item?.description || "",
        }));
        setInstrumentsCount(res.data?.data?.count || 0);

        setInstruments(newData || []);
        setTableLoader(false);
      } catch (err) {
        setTableLoader(false);
        errorToastMessage(err as Error);
      }
    },
    [setTableLoader, setInstrumentsCount, setInstruments]
  );

  useEffect(() => {
    fetchInstruments(0);
  }, [fetchInstruments]);

  const search = () => {
    setPage(0);
    fetchInstruments(0, searchText);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
    fetchInstruments(newPage, searchText);
  };

  const modifySearchTerm = (val: string) => {
    setSearchText(val);
  };

  const uploadCSV = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        toastMessage("warning", "File size must smaller than 5MB!");
        return false;
      }
      dispatch(importConsentBlocks(file));
      closeModal();
    }
  };

  const importViaUrl = async (id: string) => {
    try {
      setButtonLoader(id);
      const res: AxiosResponse = await http.get(`/instrument-library/${id}`);
      const data = res.data?.data;
      const fileUrl = data.fileUrl;
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const file = new File([blob], "questions.xml", { type: "text/xml" });
      dispatch(importConsentBlocks(file));
      closeModal();
    } catch (err) {
      errorToastMessage(err as Error);
      setButtonLoader("");
    }
  };

  // const viewInstrumentDetails = (data: any) => {
  //   setInstrumentDetails(data);
  // };

  // const backToImport = () => {
  //   setInstrumentDetails(null);
  // };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box
        sx={{
          ...ModalDrawerStyles,
          left: "40%",
          width: {
            xs: "96vw",
            md: "60vw",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* {instrumentDetails && (
              <ChevronLeft
                onClick={backToImport}
                sx={{ cursor: "pointer", mr: 1, color: "text.secondary" }}
                fontSize="large"
              />
            )} */}
            <Typography sx={ModalHeaderTitleStyles}>Import Consent</Typography>
          </Box>
          <IconButton onClick={closeModal}>
            <CloseRounded sx={{ color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1, mb: 2 }} />

        <Box style={DrawerForm}>
          <Box>
            <FormControl sx={{ ...InputWrapper, mb: 2 }}>
              <FormLabel sx={LabelStyle} htmlFor="file-upload">
                Import file
              </FormLabel>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  // sx={{ height: "54px" }}
                >
                  <span>Import Consent</span>
                  <input
                    hidden
                    accept="text/xml, application/xml, application/xhtml+xml"
                    type="file"
                    onChange={uploadCSV}
                  />
                </Button>
              </Box>
            </FormControl>
            <Box sx={{ ml: 5, mb: 2 }}>
              <Typography variant="subtitle1">OR</Typography>
            </Box>
            <Stack gap={2} direction={"row"}>
              <FormControl sx={{ ...InputWrapper, mb: 2, width: "35%" }}>
                <FormLabel sx={LabelStyle} htmlFor="title">
                  Title
                </FormLabel>
                <TextField
                  placeholder="Title"
                  style={{ width: "100%" }}
                  value={searchText}
                  onChange={(e) => modifySearchTerm(e.target.value)}
                />
              </FormControl>
              {/* <FormControl sx={{ ...InputWrapper, mb: 2, width: "35%" }}>
                  <FormLabel sx={LabelStyle} htmlFor="type">
                    Type
                  </FormLabel>
                  <Select
                    id="type"
                    fullWidth
                    value={filterId || ""}
                    onChange={(e) => {
                      setFilterId(e.target.value);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: "300px", overflowY: "auto" },
                      },
                    }}
                  >
                    <MenuItem key="clear" value="">
                      Clear selection
                    </MenuItem>
                    {artifactsTypes?.map((option: any) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
              <Button
                variant="contained"
                onClick={search}
                sx={{ alignSelf: "flex-end", mb: 2, height: "48px" }}
              >
                Search
              </Button>
            </Stack>
            <Box sx={{ mb: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Title</StyledTableCell>
                    {/* <StyledTableCell>Disease Area</StyledTableCell>
                      <StyledTableCell>Source/Location</StyledTableCell>
                      <StyledTableCell>Approximate Cost</StyledTableCell> */}
                    <StyledTableCell>Description</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                {!tableLoader && instruments?.length > 0 && (
                  <>
                    <TableBody>
                      {instruments?.map((row: any) => (
                        <TableRow key={row?.id}>
                          <StyledTableCell>{row?.title || "-"}</StyledTableCell>
                          {/* <StyledTableCell>
                              {row?.diseaseArea || "-"}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.source || "-"}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row?.cost || "-"}
                            </StyledTableCell> */}
                          <StyledTableCell>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: row.description || "-",
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              {buttonLoader === row?.id ? (
                                <CircularProgress size={18} sx={{ ml: 1 }} />
                              ) : (
                                <>
                                  {/* <Button
                                      onClick={() => viewInstrumentDetails(row)}
                                      disabled={!!buttonLoader}
                                    >
                                      View
                                    </Button> */}
                                  <Button
                                    onClick={() => importViaUrl(row?.id)}
                                    disabled={!!buttonLoader}
                                    variant="outlined"
                                  >
                                    Import
                                  </Button>
                                </>
                              )}
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    {instrumentsCount > 10 && (
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            sx={TablePaginationStyle}
                            count={instrumentsCount}
                            page={page}
                            rowsPerPage={10}
                            rowsPerPageOptions={[10]}
                            onPageChange={handlePageChange}
                            labelDisplayedRows={paginationLabel}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </>
                )}
              </Table>
              {!tableLoader && instruments.length === 0 && (
                <NoDataContainer>
                  <Typography variant="body1" color="gray">
                    No Data
                  </Typography>
                </NoDataContainer>
              )}
              {tableLoader && (
                <Box sx={{ width: "100%" }}>
                  <LinearProgress />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImportConsentBlocksModal;
